/**
 * Scroll lock manager
 * Handles locking scroll position until action points are clicked.
 *
 * Two modes:
 *   1. Default — locks at the image bottom (or sticky point) until ALL action points are clicked.
 *   2. Progressive (scrollLockMargin set) — locks at each successive action point's Y position
 *      plus a configurable viewport-% margin. After clicking one, scroll extends to the next.
 */

import { scrollManager } from '../../../utils/scroll'

export interface ScrollLockContext {
  imageContainer: HTMLDivElement | null
  speechBubbles: Array<{ id: string; actionPoint?: { position?: { x: number; y: number } } }>
  clickedActionPointIndices: Set<number>
  scrollLockMargin?: number
}

export interface ScrollLockHandle {
  recalculate(): void
  destroy(): void
}

export function setupScrollLock(context: ScrollLockContext): ScrollLockHandle {
  let destroyed = false
  let scrollHandler: (() => void) | null = null
  let resizeHandler: (() => void) | null = null
  let recalcTimer: ReturnType<typeof setInterval> | null = null

  const handle: ScrollLockHandle = {
    recalculate: () => {},
    destroy: () => {
      destroyed = true
      if (scrollHandler) window.removeEventListener('scroll', scrollHandler)
      if (resizeHandler) window.removeEventListener('resize', resizeHandler)
      if (recalcTimer) { clearInterval(recalcTimer); recalcTimer = null }
      scrollHandler = null
      resizeHandler = null
      scrollManager.removeMaxScroll()
    },
  }

  if (!context.imageContainer) {
    return handle
  }

  const setup = () => {
    if (destroyed) return
    if (!context.imageContainer?.isConnected) {
      setTimeout(setup, 50)
      return
    }
    const img = context.imageContainer?.querySelector('img') as HTMLImageElement | null
    if (img && !(img.naturalWidth > 0)) {
      setTimeout(setup, 50)
      return
    }

    requestAnimationFrame(() => {
      if (destroyed) return

      const isUnlocked = () => {
        const { speechBubbles, clickedActionPointIndices } = context
        if (speechBubbles.length === 1 && clickedActionPointIndices.has(0)) return true
        if (speechBubbles.length > 1 && clickedActionPointIndices.size >= speechBubbles.length) return true
        return false
      }

      const stickyContainer = context.imageContainer!.closest('.sticky-layout-container') as HTMLElement | null

      const computeMaxScroll = (): number | null => {
        if (isUnlocked()) return null

        if (context.scrollLockMargin != null) {
          const nextIdx = context.clickedActionPointIndices.size
          if (nextIdx < context.speechBubbles.length) {
            const nextBubble = context.speechBubbles[nextIdx]
            const yPct = nextBubble?.actionPoint?.position?.y ?? 0
            const containerRect = context.imageContainer!.getBoundingClientRect()
            const absY = window.scrollY + containerRect.top + (yPct / 100) * containerRect.height
            const vh = window.innerHeight
            const margin = (context.scrollLockMargin / 100) * vh
            return absY - vh + margin
          }
        }

        if (stickyContainer) {
          const wrapper = stickyContainer.querySelector('.scene-wrapper') as HTMLElement | null
          const stickyTopPx = parseFloat(wrapper?.style.top || '0')
          const containerAbsTop = stickyContainer.getBoundingClientRect().top + window.scrollY
          return containerAbsTop - stickyTopPx
        }

        const containerRect = context.imageContainer!.getBoundingClientRect()
        return window.scrollY + containerRect.top + containerRect.height
      }

      const applyLimit = () => {
        if (destroyed) return
        const target = computeMaxScroll()
        if (target === null) {
          scrollManager.removeMaxScroll()
          if (scrollHandler) window.removeEventListener('scroll', scrollHandler)
          if (resizeHandler) window.removeEventListener('resize', resizeHandler)
          scrollHandler = null
          resizeHandler = null
          return
        }
        scrollManager.setMaxScroll(target)
      }

      let rafPending = false
      const scheduleApply = () => {
        if (rafPending) return
        rafPending = true
        requestAnimationFrame(() => {
          rafPending = false
          applyLimit()
        })
      }

      scrollHandler = scheduleApply
      resizeHandler = scheduleApply

      handle.recalculate = applyLimit

      window.addEventListener('scroll', scrollHandler, { passive: true })
      window.addEventListener('resize', resizeHandler, { passive: true })

      applyLimit()

      recalcTimer = setInterval(() => {
        if (destroyed) {
          clearInterval(recalcTimer!); recalcTimer = null
          return
        }
        applyLimit()
        if (!scrollHandler) {
          clearInterval(recalcTimer!); recalcTimer = null
        }
      }, 500)
    })
  }

  requestAnimationFrame(setup)

  return handle
}
